import React, { useMemo } from 'react';
import { HashRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AuthProvider } from '../../contexts/AuthContext';
import { useAuth } from '../../hooks/useAuth';

import DashboardPage from './routes/DashboardPage/DashboardPage';
import LoginPage from './routes/LoginPage';
import RegisterPage from './routes/RegisterPage/RegisterPage';

import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

function PageLoader() {
    return (
        <div className="minside-page">
            <div className="data-loader">
                <FontAwesomeIcon icon={faSpinner} spin size="xl" />
            </div>
        </div>
    );
}

function AuthGate() {
    const { loading, loggedIn, hasProfile } = useAuth();
    const location = useLocation();

    const destination = useMemo(() => {
        if (loading) return null;
        if (!loggedIn) return '/login';
        if (loggedIn && !hasProfile) return '/register';
        return '/dashboard';
    }, [loading, loggedIn, hasProfile]);

    if (loading) return <PageLoader />;

    return <Navigate to={destination} state={{ from: location }} replace />;
}

function RequireLogin({ children }) {
    const { loading, loggedIn } = useAuth();
    const location = useLocation();

    if (loading) return <PageLoader />;
    if (!loggedIn) return <Navigate to="/login" state={{ from: location }} replace />;

    return children;
}

function RequireProfile({ children }) {
    const { loading, loggedIn, hasProfile } = useAuth();
    const location = useLocation();

    if (loading) return <PageLoader />;
    if (!loggedIn) return <Navigate to="/login" state={{ from: location }} replace />;
    if (!hasProfile) return <Navigate to="/register" state={{ from: location }} replace />;

    return children;
}

function NotFoundPage() {
    return (
        <main id="content" className="site-main">
            <section className="error-404 content">
                <img
                    src="../wp-content/themes/sar61n-theme/assets/img/logo-large.svg"
                    alt="SAR61˚N logo"
                    className="logo-404"
                />
                <h1 className="wp-block-heading has-text-align-center has-display-font-size">
                    404 - Siden finnes ikke
                </h1>
                <p className="has-text-align-center">
                    Beklager, siden du leter etter ble ikke funnet.
                </p>
            </section>
        </main>
    );
}

function MinSideRoutes() {
    return (
        <Routes>
            <Route path="/" element={<AuthGate />} />
            <Route path="/login" element={<LoginPage />} />

            <Route
                path="/register"
                element={
                    <RequireLogin>
                        <RegisterPage />
                    </RequireLogin>
                }
            />

            <Route
                path="/dashboard"
                element={
                    <RequireProfile>
                        <DashboardPage />
                    </RequireProfile>
                }
            />

            <Route path="*" element={<NotFoundPage />} />
        </Routes>
    );
}

function MinSide() {
    return (
        <AuthProvider>
            <HashRouter>
                <MinSideRoutes />
            </HashRouter>
        </AuthProvider>
    );
}

export default MinSide;
